import ApiClient from '@/util/ApiClient'
import { defineStore } from 'pinia'

export type ViewSettingsStoreState = {
  spaceListViewIds: { [spaceListId: string]: string | null }
  blockOverviewVideo: boolean
  emojiUsage: { [emoji: string]: number }
  firstSpaceOnboardingDismissed: boolean
}

export const useViewSettingsStore = defineStore('viewSettings', {
  state: (): ViewSettingsStoreState => ({
    spaceListViewIds: {},
    blockOverviewVideo: false,
    emojiUsage: {},
    firstSpaceOnboardingDismissed: false
  }),
  getters: {
    frequentEmojis(): string[] {
      return Object.entries(this.emojiUsage)
        .sort(([, countA], [, countB]) => countB - countA)
        .slice(0, 8)
        .map(([emoji]) => emoji)
    }
  },
  actions: {
    async fetchViewSettings(): Promise<ViewSettingsStoreState> {
      const viewSettings = (await ApiClient.get<ViewSettingsStoreState>(`/view-settings`)).data
      this.$state = viewSettings
      return viewSettings
    },
    async updateViewSettings(newSettings: Partial<ViewSettingsStoreState>): Promise<ViewSettingsStoreState> {
      const updatedSettings = { ...this.$state, ...newSettings }
      const viewSettings = (await ApiClient.post<ViewSettingsStoreState>(`/view-settings`, updatedSettings)).data
      this.$state = viewSettings
      return viewSettings
    },
    async incrementEmojiUsage(emoji: string) {
      const updatedEmojiUsage = this.emojiUsage
      if (updatedEmojiUsage[emoji]) {
        updatedEmojiUsage[emoji]++
      } else {
        updatedEmojiUsage[emoji] = 1
      }
      this.updateViewSettings({ emojiUsage: updatedEmojiUsage })
    },
    async setSpaceListViewId(spaceListId: string, spaceListViewId: string) {
      const updatedSpaceListViewIds = { ...this.spaceListViewIds, [spaceListId]: spaceListViewId }
      this.spaceListViewIds = updatedSpaceListViewIds
      this.updateViewSettings({ spaceListViewIds: updatedSpaceListViewIds })
    }
  }
})

export type ViewSettingsStore = ReturnType<typeof useViewSettingsStore>
